/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { memo, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FocusScope } from 'react-aria';
import { RemoveScroll } from 'react-remove-scroll';
import { useLocation, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useHover } from 'react-aria';

import usePageNavigation from './usePageNavigation';
import { Typography } from '../typography';
import Header from '../layout/Header';
import Burger from './Burger';
import Container from '../layout/Container';
import { useNavigation, usePage } from '../../api/use';
import { CombinedLogos } from '../Logos';

const variants = {
	container: {
		on: {
			opacity: 1,
			transition: {
				staggerChildren: 0.25,
				when: 'beforeChildren',
			},
		},
		off: {
			opacity: 0,
			transition: {
				staggerChildren: 0.1,
				staggerDirection: -1,
				when: 'afterChildren',
			},
		},
	},
	item: {
		on: {
			y: '0%',
			scale: 1,
			opacity: 1,
		},
		off: {
			y: '100%',
			scale: 0.8,
			opacity: 0,
		},
	},
	lineActive: {
		on: {
			scaleX: 1.0,
		},
		off: {
			scaleX: 0.0,
		},
	},
	lineInactive: {
		on: {
			scaleX: 0.0,
		},
		off: {
			scaleX: 0.0,
		},
	},
};

const Line = ({ active }) => {
	return (
		<motion.div
			initial={{ scaleX: 0.0 }}
			animate={{ scaleX: active ? 1.0 : 0.0 }}
			exit={{ scaleX: 0.0 }}
			css={(theme) => [
				theme.typography.getStyle({ theme }),
				css`
					position: relative;
					left: 25%;
					width: 50%;
					height: 1px;
					background: currentColor;
					margin: 0.4em 0;
				`,
			]}
		/>
	);
};

const Section = ({ id, hash, label, hiliteId, scrollToItemById }) => {
	const ref = useRef();
	const isCurrent = useMemo(() => hiliteId === id, [id, hiliteId]);
	const onClick = useCallback(() => {
		scrollToItemById(id);
	}, [scrollToItemById, id]);
	const { hoverProps, isHovered } = useHover({});

	return (
		<motion.div
			ref={ref}
			key={id}
			variants={variants.item}
			css={css`
				position: relative;
				display: inline-block;
				padding: 0;
				margin: 0 0.4em 0.2em;
			`}
		>
			<Typography
				{...hoverProps}
				as={Link}
				to={{ hash }}
				weight="thin"
				size="s"
				css={(theme) =>
					css`
						letter-spacing: 0.1em;
						text-transform: uppercase;
						line-height: 1;
						text-decoration: none;
						color: ${theme.color.primaryContrast};

						&:visited {
							color: ${theme.color.primaryContrast};
						}
					`
				}
				onClick={onClick}
			>
				{label}
			</Typography>
			<Line active={!!isCurrent || isHovered} />
		</motion.div>
	);
};

const Page = ({ page, id, items, hiliteId, scrollToItemById }) => {
	const { hoverProps, isHovered } = useHover({});

	return (
		<motion.li
			key={page.id}
			variants={variants.item}
			css={css`
				position: relative;
			`}
		>
			<Typography
				as={Link}
				{...hoverProps}
				to={page.link}
				size={page.isStory ? 'l' : 'm'}
				weight="thin"
				css={css`
					letter-spacing: 0.04em;
					display: inline-block;
					margin: 0 0.6em;
					text-transform: uppercase;
					line-height: 1;
					color: inherit;

					&:visited,
					&:active {
						color: inherit;
					}
				`}
			>
				{page.menuTitle}
			</Typography>
			<Line active={page.id === id || isHovered} />
			<div
				css={css`
					margin-bottom: 3em;
				`}
			>
				{id === page.id &&
					items.map((it) => (
						<Section
							key={it.id}
							id={it.id}
							label={it.label}
							hash={it.hash}
							hiliteId={hiliteId}
							scrollToItemById={scrollToItemById}
						/>
					))}
			</div>
		</motion.li>
	);
};

const NavigationContent = ({ enabled, onClose }) => {
	const { hierarchy } = useNavigation();
	const { id } = usePage();
	const { items, hiliteId, scrollToItemById } = usePageNavigation();
	const [stories, pages] = useMemo(
		() => [hierarchy.filter((p) => !!p.isStory), hierarchy.filter((p) => !p.isStory)],
		[hierarchy]
	);
	return (
		<AnimatePresence>
			{!!enabled && (
				<RemoveScroll
					as={motion.div}
					variants={variants.container}
					initial="off"
					animate="on"
					exit="off"
					aria-hidden={!enabled}
					enabled={enabled}
					css={(theme) => css`
						pointer-events: all;
						position: absolute;
						top: 0;
						left: 0;
						width: 100vw;
						height: 100vh;
						overflow: auto;
						z-index: ${theme.zIndex.mobileNav};
						color: ${theme.color.primaryDarkContrast};
						backdrop-filter: blur(1vw);
						background-color: rgba(7, 72, 147, 0.2);
					`}
				>
					<Header isSticky>
						<Burger isOpen onClick={onClose} />
					</Header>
					<Container narrow>
						<ul
							css={css`
								text-align: center;
								list-style: none;
								margin: 3em 0 0 0;
								padding: 0 0 2em;
							`}
						>
							{stories.map((page) => (
								<Page
									key={page.id}
									page={page}
									id={id}
									items={items}
									hiliteId={hiliteId}
									scrollToItemById={scrollToItemById}
								/>
							))}
							<li>
								<ul
									css={css`
										display: flex;
										flex-wrap: wrap;
										justify-content: center;
										list-style: none;
										margin: 3em 0 0 0;
										padding: 0 0 2em;
									`}
								>
									{pages.map((page) => (
										<Page
											key={page.id}
											page={page}
											id={id}
											items={items}
											hiliteId={hiliteId}
											scrollToItemById={scrollToItemById}
										/>
									))}
								</ul>
							</li>
						</ul>
						<CombinedLogos isActive={enabled} />
					</Container>
				</RemoveScroll>
			)}
		</AnimatePresence>
	);
};

const Navigation = () => {
	const { loaded } = useNavigation();
	const [showDialog, setShowDialog] = useState(false);
	const open = useCallback(() => setShowDialog(true), [setShowDialog]);
	const close = useCallback(() => setShowDialog(false), [setShowDialog]);
	const location = useLocation();

	useEffect(() => {
		setShowDialog(false);
	}, [location, setShowDialog]);

	return (
		!!loaded && (
			<>
				<Burger isOpen={showDialog} onClick={open} />
				<FocusScope contain={showDialog}>
					<NavigationContent enabled={showDialog} onClose={close} />
				</FocusScope>
			</>
		)
	);
};

export default memo(Navigation);
